import { ColumnsProps, useWindowResize } from '@/Helper';
import { getNextPage } from '@/components/alarms/helpers';
import { EventPaginationSetting } from '@/model/AlarmModel';
import { ContextMenuContent, EventViewerActions, EventViewerContextOptions } from '@/model/EventModel';
import React, { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { VariableSizeList as List } from 'react-window';
import { ContextMenu } from '../ContextMenu/ContextMenu';
import { NonActionHeaderColumn } from './NonActionHeaderColumn';
import { RefWrap } from './RefWrap';
import { Row } from './Row';
import styles from './virtualTable.module.scss';

const minimumRowHeight: number = 30;
const defaultContextContent = { options: [], record: undefined, visible: false };

type VirtualTableProps<T> = {
	columnsWidth: CSSProperties;
	columns: ColumnsProps<T>[];
	dataSource: T[];
	contextMenuOptions?: EventViewerContextOptions<T>;
	backgroundColor?: CSSProperties;
	maxHeight: number;
	isEventViewer?: boolean;
	customRow?: React.ReactNode;
	fetchData: (paginationSettings: EventPaginationSetting, reset?: boolean) => void;
	paginationSettings: EventPaginationSetting;
	onSort: (paginationSettings: EventPaginationSetting) => void;
};

type sizeMap = {
	[index: number]: number;
};

const VirtualTable = <T extends {}>({
	columns,
	dataSource,
	columnsWidth,
	contextMenuOptions,
	backgroundColor,
	maxHeight,
	fetchData,
	isEventViewer,
	paginationSettings,
	onSort,
}: VirtualTableProps<T>) => {
	//#region State Management Variables
	const [headerScroll, setHeaderScroll] = useState<number>(0);
	const [height, setHeight] = useState<number>(minimumRowHeight);
	const [contextMenuContent, setContextMenuContent] = useState<ContextMenuContent<T>>(defaultContextContent);
	const [selectedRowKey, setSelectedRowKey] = useState<string>('');

	const listRef = useRef(undefined);
	const headerRef = useRef(undefined);
	const sizeMap = useRef<sizeMap>({});
	const setSize = useCallback((index: number, size: number) => {
		sizeMap.current = { ...sizeMap.current, [index]: size };
		if (listRef?.current) {
			listRef.current.resetAfterIndex(index);
		}
	}, []);
	const [windowWidth] = useWindowResize();
	//#endregion

	useEffect(() => {
		if (headerRef?.current) {
			headerRef.current.scrollLeft = headerScroll;
		}
	}, [headerScroll]);

	useEffect(() => {
		if (listRef?.current && dataSource?.length > 0) {
			listRef.current.scrollToItem(0);
		}
	}, [height]);

	const getSize = (index: number): number => sizeMap.current[index] || minimumRowHeight;

	const onFetchData = (): void => {
		const quantityOfEvents = dataSource?.length;
		const newPage = getNextPage(quantityOfEvents, paginationSettings.PageSize);

		if (paginationSettings.PageNumber != newPage) {
			fetchData({
				...paginationSettings,
				PageNumber: newPage,
			});
		}
	};

	const handleInnerRef = ref => {
		if (ref?.children) {
			setTimeout(() => {
				let totalHeight: number = 0;
				for (let index = 0; index < ref.children.length; index++) {
					const elementDiv = ref.children[index];
					if (elementDiv && elementDiv.style.height) {
						const heightNumber: number = Number(elementDiv.style.height.toLowerCase().split('px')[0]);
						if (!isNaN(heightNumber) && heightNumber <= maxHeight && heightNumber >= 2) {
							totalHeight += heightNumber;
						} else {
							return;
						}
					}
				}

				if (totalHeight === 0) {
					setHeight(minimumRowHeight - 2);
				} else if (totalHeight <= maxHeight && totalHeight >= 2) {
					setHeight(totalHeight - 2);
				} else if (height !== maxHeight) {
					setHeight(maxHeight);
				}
			}, 0);
		}
	};

	const listKey: string = dataSource.length === 0 && isEventViewer ? `${Math.floor(Math.random() * 100)}` : undefined;
	const listComponent: JSX.Element = (
		<RefWrap fetchData={onFetchData} headerScroll={headerScroll} setHeaderScroll={setHeaderScroll}>
			<List
				ref={listRef}
				height={height}
				width='100%'
				itemCount={dataSource.length}
				itemSize={getSize}
				itemData={dataSource}
				innerRef={handleInnerRef}
				className={isEventViewer ? styles.list : styles.customList}
				overscanCount={10}
				style={backgroundColor}
			>
				{({ data, index, style }) => (
					<div style={style}>
						<Row
							data={data}
							index={index}
							setSize={setSize}
							selectedRowKey={selectedRowKey}
							setSelectedRowKey={setSelectedRowKey}
							windowWidth={windowWidth}
							columns={columns}
							columnsWidth={columnsWidth}
							contextMenuOptions={contextMenuOptions}
							backgroundColor={backgroundColor}
							setContextMenuContent={setContextMenuContent}
						/>
					</div>
				)}
			</List>
		</RefWrap>
	);

	return (
		<div className={styles.container}>
			<div key={1} ref={headerRef} className={styles.header} style={columnsWidth}>
				{columns.map(column => (
					<NonActionHeaderColumn
						column={column}
						onSort={onSort}
						paginationSettings={paginationSettings}
						key={`header-column-${String(column.dataIndex)}`}
						separator={false}
					/>
				))}
			</div>
			<React.Fragment key={listKey}>
				{isEventViewer && contextMenuOptions ? (
					<ContextMenu
						options={contextMenuContent.options}
						onClickOption={(value: EventViewerActions) => {
							setSelectedRowKey('');
							contextMenuOptions.onClickOption(value, contextMenuContent.record);
							setContextMenuContent(defaultContextContent);
						}}
						open={contextMenuContent.visible}
						children={<div style={backgroundColor}>{listComponent}</div>}
						onOpenChange={(open: boolean) => {
							if (open) {
								setSelectedRowKey(contextMenuContent.record['key']);
							} else {
								setContextMenuContent(defaultContextContent);
								setSelectedRowKey('');
							}
						}}
					/>
				) : (
					<div style={backgroundColor}>{listComponent}</div>
				)}
			</React.Fragment>
		</div>
	);
};

export { VirtualTable };
